import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from "react-helmet-async";

import imageright from '../assets/img/coudounat.webp';
import handleSendEmail from '../services/email';

const quinceImg = '/assets/img/quince.png';

const ContactPage = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState({ type: '', message: '' });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const validateForm = () => {
    const { name, email, message } = formData;
    if (!name || !email || !message) {
      setFeedbackMessage({ type: 'error', message: t('contact.form.validation.missing') });
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setFeedbackMessage({ type: 'error', message: t('contact.form.validation.invalid_email') });
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    setFeedbackMessage({});
    try {
      await handleSendEmail(formData);
      setFeedbackMessage({ type: 'success', message: t('contact.form.success') });
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      setFeedbackMessage({ type: 'error', message: t('contact.form.error') });
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    "w-full rounded-xl border bg-white px-4 py-3 font-nunito text-stone-800 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-amber-500/40 focus:border-amber-500 transition";

  return (
    <div style={{ backgroundColor: '#FBF7EF' }}>
      <Helmet>
        <title>Contact | Au Coing du Ventoux</title>
        <meta name="description" content="Une question sur nos produits, une demande de revente ou un partenariat ? Contactez directement la distillerie Au Coing du Ventoux, au pied du Mont Ventoux." />
        <meta property="og:title" content="Contactez Au Coing du Ventoux" />
        <meta property="og:description" content="Échangeons autour de nos liqueurs et eaux-de-vie de coing artisanales. Réponse rapide directement par le producteur." />
        <meta property="og:url" content="https://aucoingduventoux.com/contact" />
        <link rel="canonical" href="https://aucoingduventoux.com/contact" />
      </Helmet>

      <div className="relative overflow-hidden pt-40 md:pt-52 pb-24">
        {/* Coings decoratifs sur les cotes */}
        <img src={quinceImg} alt="" aria-hidden="true" loading="lazy"
          className="hidden md:block absolute top-44 -right-10 w-28 opacity-[0.06] pointer-events-none" />
        <img src={quinceImg} alt="" aria-hidden="true" loading="lazy"
          className="hidden md:block absolute bottom-40 -left-10 w-24 opacity-[0.06] pointer-events-none" />

        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="flex flex-col md:flex-row gap-10 lg:gap-16 items-start">

            {/* Colonne texte + formulaire */}
            <div className="w-full md:w-1/2">
              <span className="text-xs font-semibold tracking-[0.3em] uppercase text-amber-800">
                Contact
              </span>
              <h1 className="font-sans mt-4 text-4xl md:text-5xl text-stone-900 leading-tight">
                {t('contact.titleoui')}
              </h1>

              <p className="mt-6 font-nunito text-stone-600 leading-relaxed">{t('contact.description1')}</p>

              <form onSubmit={handleSubmit} className="mt-10 space-y-4">
                <input type="text" name="name" placeholder={t('contact.form.name')} value={formData.name} onChange={handleChange} className={inputClass} required />
                <input type="email" name="email" placeholder={t('contact.form.email')} value={formData.email} onChange={handleChange} className={inputClass} required />
                <input type="text" name="phone" placeholder={t('contact.form.phone')} value={formData.phone} onChange={handleChange} className={inputClass} />
                <textarea name="message" placeholder={t('contact.form.message')} value={formData.message} onChange={handleChange} className={`${inputClass} h-36`} required />

                {feedbackMessage.message && (
                  <div
                    className="rounded-xl p-4 font-nunito text-sm"
                    style={
                      feedbackMessage.type === 'error'
                        ? { backgroundColor: 'rgba(180,69,46,0.1)', color: '#B4452E' }
                        : { backgroundColor: 'rgba(79,122,58,0.12)', color: '#3F6230' }
                    }
                  >
                    {feedbackMessage.message}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className={`w-full font-sans tracking-wide rounded-xl py-4 text-white transition-all duration-300 ${
                    loading ? 'opacity-70 cursor-not-allowed' : 'hover:opacity-90 hover:scale-[1.01]'
                  }`}
                  style={{ backgroundColor: '#2E2318' }}
                >
                  {loading ? t('contact.form.sending') : t('contact.form.submit')}
                </button>
              </form>
            </div>

            {/* Image (panneau encadre, colle au scroll sur desktop) */}
            <div className="w-full md:w-1/2 md:sticky md:top-32 order-first md:order-last">
              <div
                className="rounded-3xl overflow-hidden shadow-lg"
                style={{ background: 'linear-gradient(160deg, #EFE2CB 0%, #E4D2B2 100%)' }}
              >
                <img
                  src={imageright}
                  alt="Au Coing du Ventoux"
                  loading="lazy"
                  decoding="async"
                  className="w-full h-[360px] md:h-[640px] object-cover"
                />
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;